import { CartAddEvent } from '../../assets/events';

class QuickOrderList extends HTMLElement {

  #abortController = new AbortController();

  constructor() {
    super();
    this.cart = [];
    this.variants = {};
  }

  connectedCallback() {
    this.animateAddToCart = this.dataset.addToCartAnimation === 'true';
    this.setupVariantData();
    this.setupEventListeners();
    this.updateTotal();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  setupVariantData() {
    const variants = this.querySelectorAll('.quick-order-variant');
    variants.forEach(variant => {
      const variantId = variant.dataset.variantId;
      const price = parseInt(variant.dataset.variantPrice, 10);
      this.variants[variantId] = { price: price };
    });

  }

  setupEventListeners() {
    const { signal } = this.#abortController;
    this.addEventListener('click', this.handleButtonClick.bind(this), {signal});
    this.addEventListener('change', this.handleQuantityChange.bind(this), {signal});
  }

  handleButtonClick( event ) {
    const target = event.target;

    if (target.classList.contains('quick-order-increase')) {
      this.updateQuantity(target.dataset.variantId, 1);
    }
    else if (target.classList.contains('quick-order-decrease')) {
      this.updateQuantity(target.dataset.variantId, -1);
    }
    else if (target.classList.contains('quick-order-add-all-button')) {
      void this.addAllToCart();
    }
  }

  handleQuantityChange( event ) {
    if (event.target.classList.contains('quick-order-quantity-input')) {
      const moq = parseInt(event.target.dataset.moq)|| 1;
      const variantId = event.target.dataset.variantId;
      let quantity = parseInt(event.target.value) || 0;

      if (quantity > 0 && quantity < moq) {
        quantity = moq;
        event.target.value = moq;
      }

      this.setQuantity(variantId, quantity);
      this.updateTotal();
      this.updateAddAllButton();
    }
  }

  updateQuantity( variantId, change ) {
    const input = this.querySelector(`input[data-variant-id="${variantId}"]`);
    if (input) {
      const moq = parseInt(input.dataset.moq);
      const currentValue = parseInt(input.value) || 0;

      let initValue = currentValue;
      let newValue = currentValue;

      if (change === 1) {
        newValue = currentValue === 0 ? moq : currentValue + 1;
      }
      else if (change === -1) {
        newValue = currentValue === moq ? 0 : Math.max(0, currentValue - 1);
      }

      input.value = newValue;
      if (!(initValue === 0 && change === -1) && this.animateAddToCart) {
        this.#animateFlyToDrawer(input, change);
      }
      this.setQuantity(variantId, newValue);
    }
  }

  setQuantity( variantId, quantity ) {
    const existingIndex = this.cart.findIndex(item => item.id === variantId);

    if (quantity > 0) {
      if (existingIndex >= 0) {
        this.cart[existingIndex].quantity = parseInt(quantity);
      }
      else {
        this.cart.push({
          id: variantId,
          quantity: parseInt(quantity),
        });
      }
    }
    else {
      if (existingIndex >= 0) {
        this.cart.splice(existingIndex, 1);
      }
    }

    this.updateTotal();
    this.updateAddAllButton();
  }

  updateTotal() {
    let total = 0;
    this.cart.forEach(item => {
      if (this.variants[item.id]) {
        total += this.variants[item.id].price * item.quantity;
      }
    });
    const totalElement = this.querySelector('.quick-order-total-amount');
    if (totalElement) {
      totalElement.textContent = new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP',
        minimumFractionDigits: 2,
      }).format(total / 100);
    }
  }

  updateAddAllButton() {
    const button = this.querySelector('.quick-order-add-all-button');
    if (button) {
      button.disabled = this.cart.length === 0;
    }
  }

  #animateFlyToCart() {
    const addToCartButton = this.querySelector('.quick-order-add-all-button');
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.querySelector('.quick-order-variant-image img').src;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  #animateFlyToDrawer(source, change) {
    const drawer = document.querySelector('.quick-order-total');
    // find the image closest to the input
    let image = source.closest('.quick-order-variant').querySelector('.quick-order-variant-image img');
    if(!image){
      //if there's not a product image then use the first one available
      image = this.querySelector('.quick-order-variant-image img');
    }

    if (!drawer || !source || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image.src})`);
    if(change === 1) {
      flyToCartElement.source = source;
      flyToCartElement.destination = drawer;
    }else{
      flyToCartElement.source = drawer;
      flyToCartElement.destination = source;
    }

    document.body.appendChild(flyToCartElement);
  }

  async addAllToCart() {
    if (this.cart.length === 0) {
      return;
    }

    const button = this.querySelector('.quick-order-add-all-button');

    if (!button) {
      console.error('Add all button not found');
      return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: this.cart,
        }),
      });

      if (!response.ok) {
        let errorMsg = 'Failed to add items to cart';
        try {
          const errorData = await response.json();
          errorMsg = errorData.message || errorMsg;
        }
        catch {
          // Ignore JSON parse errors
        }
        throw new Error(errorMsg);
      }

      const newCart = await fetch(window.Shopify.routes.root + 'cart.js');
      const newCartObject = await newCart.json();

      this.cart = [];
      this.querySelectorAll('.quick-order-quantity-input').forEach(input => {
        input.value = '0';
      });
      this.updateTotal();
      this.updateAddAllButton();
      button.textContent = originalText;
      if(this.animateAddToCart) {
        this.#animateFlyToCart();
      }
      this.dispatchEvent(
        new CartAddEvent({}, null, {
          source: 'canape-menu-builder',
          itemCount: newCartObject.item_count,
        }),
      );
    }
    catch (error) {
      console.error('Error adding to cart:', error);
      button.textContent = 'Error - Try again';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  }
}

customElements.define('quick-order-list', QuickOrderList);